
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

interface CycleAnalysisCardProps {
  isLoading: boolean;
  periodHistory: any[];
  averageCycleLength: number | null;
  averagePeriodLength: number | null;
}

const CycleAnalysisCard: React.FC<CycleAnalysisCardProps> = ({
  isLoading,
  periodHistory,
  averageCycleLength,
  averagePeriodLength
}) => {
  // Prepare data for chart
  const prepareChartData = () => {
    return periodHistory.slice(0, 6).map(period => {
      const periodLength = period.period_length || 
        (period.period_end_date ? 
          (new Date(period.period_end_date).getTime() - new Date(period.period_start_date).getTime()) / (1000 * 60 * 60 * 24) + 1 : 
          0);
          
      return {
        date: format(new Date(period.period_start_date), 'MMM d'),
        periodLength: periodLength,
        cycleLength: period.cycle_length || 0
      };
    }).reverse();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <BarChart3 className="mr-2 h-5 w-5 text-hormony-coral" />
          Cycle Analysis
        </CardTitle>
        <CardDescription>
          Your menstrual cycle patterns over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-40 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-hormony-coral border-t-transparent rounded-full"></div>
          </div>
        ) : periodHistory.length > 1 ? (
          <>
            <div className="h-40 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={prepareChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="periodLength" 
                    name="Period Length" 
                    stroke="#e27d5f" 
                    activeDot={{ r: 8 }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cycleLength" 
                    name="Cycle Length" 
                    stroke="#41b3a3" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-hormony-coral/10 p-3 rounded-lg">
                <p className="text-sm text-muted-foreground">Avg. Cycle Length</p>
                <p className="text-2xl font-bold">{averageCycleLength || '—'} days</p>
              </div>
              <div className="bg-hormony-coral/10 p-3 rounded-lg">
                <p className="text-sm text-muted-foreground">Avg. Period Length</p>
                <p className="text-2xl font-bold">{averagePeriodLength || '—'} days</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-3">
              Track at least 2 periods to see your cycle analysis
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CycleAnalysisCard;
